import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { InfoIcon, ShieldCheckIcon } from 'lucide-react';
import { StoreButtons } from './StoreButtons';
import { PhoneFrame } from './phone/PhoneFrame';
import { DiscoverScreen } from './screens/DiscoverScreen';
import { PlayScreen } from './screens/PlayScreen';
import { DashboardScreen } from './screens/DashboardScreen';
const ease = [0.23, 1, 0.32, 1] as const;
const HERO_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/516ef107-4d6b-4e20-b94e-27c564b09b2f.jpg';
export function Hero() {
  const ref = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  });
  const yCenter = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const ySide = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const panelScale = useTransform(scrollYProgress, [0, 1], [1, 0.93]);
  return <section ref={ref} id="top" className="relative overflow-hidden bg-forest-deep pt-28 text-white lg:pt-36" data-id="element-108" data-mp="3814c5qmge0ov">
      <div className="mx-auto max-w-7xl px-5 text-center lg:px-8" data-id="element-109" data-mp="3j6g1pzwctvil">
        <h1 className="mx-auto max-w-4xl font-display text-5xl font-bold leading-[1] tracking-[-0.04em] sm:text-6xl lg:text-[5.25rem]" data-id="element-110" data-mp="3o6alzklmjmuo">
          <RevealWords text="Turn attention" data-id="element-111" data-mp="39cpain8uf6z7" />
          <br data-id="element-112" data-mp="3e8ehhbn6zq5j" />
          <RevealWords text="into value." delay={0.1} className="text-mint" data-id="element-113" data-mp="3e7uwfm28wlna" />
        </h1>
        <motion.p initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease,
        delay: 0.25
      }} className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg" data-id="element-114" data-mp="30h9cdu1225nt">
          Discover events near you, take part in quick challenges from the brands behind them, and get rewarded. You
          decide what you share.
        </motion.p>
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease,
        delay: 0.32
      }} className="mt-10 flex justify-center" data-id="element-115" data-mp="2hl9puyqujubm">
          <StoreButtons tone="onDark" data-id="element-116" data-mp="nbl42pi3sock" />
        </motion.div>
      </div>

      <div className="relative mx-auto mt-16 h-[560px] max-w-6xl px-5 sm:h-[640px] lg:mt-20 lg:px-8" data-id="element-117" data-mp="n98ebq63t9oy">
        <motion.div style={{
        scale: panelScale
      }} className="leaf-xl absolute inset-x-5 bottom-0 top-28 overflow-hidden lg:inset-x-8" data-id="element-118" data-mp="ia5fingpvzpf">
          <img src={HERO_IMAGE} alt="" className="h-full w-full object-cover" data-id="element-119" data-mp="1azkv7qjbugro" />
          <div className="absolute inset-0 bg-forest-deep/50" data-id="element-120" data-mp="161jcj6ptcjgq" />
        </motion.div>

        <div className="relative flex justify-center" data-id="element-121" data-mp="1126pakv15adm">
          <motion.div style={{
          y: ySide,
          rotate: -7,
          scale: 0.9
        }} className="-mr-12 mt-24 hidden md:block" data-id="element-122" data-mp="110wqtuk9m06g">
            <motion.div initial={{
            opacity: 0,
            y: 60
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            ease,
            delay: 0.45
          }} data-id="element-123" data-mp="x37falufynfb">
              <PhoneFrame label="Question screen with the reason each question is asked" data-id="element-124" data-mp="3htga4a18inje">
                <PlayScreen data-id="element-125" data-mp="1xb5ped03cpfl" />
              </PhoneFrame>
            </motion.div>
          </motion.div>

          <motion.div style={{
          y: yCenter
        }} className="relative z-10" data-id="element-126" data-mp="218to7r4wn2cr">
            <motion.div initial={{
            opacity: 0,
            y: 80
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            ease,
            delay: 0.35
          }} data-id="element-127" data-mp="2697najoj31yn">
              <PhoneFrame label="AVEGA discover screen with events near you" data-id="element-128" data-mp="2fyr8vx3ml9x7">
                <DiscoverScreen data-id="element-129" data-mp="2kydrkm283ide" />
              </PhoneFrame>
            </motion.div>
          </motion.div>

          <motion.div style={{
          y: ySide,
          rotate: 7,
          scale: 0.9
        }} className="-ml-12 mt-24 hidden md:block" data-id="element-130" data-mp="2p08v7x7mm7uc">
            <motion.div initial={{
            opacity: 0,
            y: 60
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            ease,
            delay: 0.5
          }} data-id="element-131" data-mp="2tyir6a16dch0">
              <PhoneFrame label="Personal dashboard with entries and prizes" data-id="element-132" data-mp="zmlua1jyreek">
                <DashboardScreen data-id="element-133" data-mp="4auqyxhtlbqy" />
              </PhoneFrame>
            </motion.div>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.25,
        ease,
        delay: 0.7
      }} className="absolute left-10 top-48 z-20 hidden items-center gap-2.5 rounded-full bg-white py-2 pl-2 pr-4 text-sm font-semibold text-ink shadow-xl xl:flex" data-id="element-134" data-mp="3vgnhqt3c1xrd">
          <span className="grid h-8 w-8 place-items-center rounded-full bg-mint" data-id="element-135" data-mp="3hnxtei0c0gr3">
            <InfoIcon className="h-4 w-4 text-forest-deep" data-id="element-136" data-mp="3ct0mbgmm4hgu" />
          </span>
          Every question says why it's asked
        </motion.div>
        <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.25,
        ease,
        delay: 0.8
      }} className="absolute right-10 top-72 z-20 hidden items-center gap-2.5 rounded-full bg-white py-2 pl-2 pr-4 text-sm font-semibold text-ink shadow-xl xl:flex" data-id="element-137" data-mp="3motwitcuzyvg">
          <span className="grid h-8 w-8 place-items-center rounded-full bg-forest" data-id="element-138" data-mp="19hcge67k5ram">
            <ShieldCheckIcon className="h-4 w-4 text-mint" data-id="element-139" data-mp="1ea5l4u310zmp" />
          </span>
          Marketing is never pre-ticked
        </motion.div>
      </div>
    </section>;
}
type RevealWordsProps = {
  text: string;
  delay?: number;
  className?: string;
};
function RevealWords({
  text,
  delay = 0,
  className = ''
}: RevealWordsProps) {
  return <span className={className} data-id="element-140" data-mp="1x883iq12jybj">
      {text.split(' ').map((word, i) => <span key={i} className="mr-[0.22em] inline-block overflow-hidden pb-[0.1em] align-bottom last:mr-0" data-id="element-141" data-mp="1x65g8drawjai" data-mpi={i}>
          <motion.span className="inline-block" initial={{
        y: '110%'
      }} animate={{
        y: 0
      }} transition={{
        duration: 0.3,
        ease,
        delay: delay + i * 0.05
      }} data-id="element-142" data-mp="1jat9f7zvf2d8">
            {word}
          </motion.span>
        </span>)}
    </span>;
}